import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { matrixUniforms } from './bgScene.js';

gsap.registerPlugin(ScrollTrigger);

export function initHeroScroll() {
  const video = document.getElementById('presenter-video');
  const heroWrapper = document.getElementById('hero-pinned-wrapper');
  const videoContainer = document.querySelector('.hero-video-container');
  let scrollStopTimer = null;

  // Initial idle state: 18% ghost opacity
  gsap.set(videoContainer, { opacity: 0.18, filter: 'grayscale(40%)' });

  window.addEventListener('scroll', () => {
    // 1. Instantly surge to 100% vivid opacity upon scroll movement
    gsap.to(videoContainer, {
      opacity: 1,
      filter: 'grayscale(0%)',
      duration: 0.15,
      ease: 'power1.out',
      overwrite: 'auto'
    });

    // 2. Clear existing idle timeout
    clearTimeout(scrollStopTimer);

    // 3. Smoothly fade back down to subtle ghost state 350ms after scroll stops
    scrollStopTimer = setTimeout(() => {
      gsap.to(videoContainer, {
        opacity: 0.18, // Settles into a sleek architectural ghost
        filter: 'grayscale(40%)',
        duration: 0.6,
        ease: 'power2.out'
      });
    }, 350);
  }, { passive: true });

  const sideCard = document.querySelector('.hero-side-card');

  const hiLayers = [
    document.getElementById('text-hi-back'),
    document.getElementById('text-hi-front')
  ];
  const nameLayers = [
    document.getElementById('text-name-back'),
    document.getElementById('text-name-front')
  ];

  if (!video || !heroWrapper) return;

  const initScrub = () => {
    const duration = video.duration || 2.4;
    const startTrim = 0.3;
    const endTrim = Math.max(startTrim + 0.1, duration - 0.08);

    if (video.readyState >= 1) {
      video.currentTime = startTrim;
    } else {
      video.addEventListener('loadedmetadata', () => {
        video.currentTime = startTrim;
      }, { once: true });
    }

    // Set initial zero states
    gsap.set(hiLayers, { opacity: 1, scale: 1 }); // "HI" clearly visible immediately
    gsap.set(nameLayers, { opacity: 0, scale: 0.92 });
    gsap.set(sideCard, { opacity: 0, y: 30, pointerEvents: 'none' });

    let targetTime = startTrim;
    let actualTime = startTrim;

    const renderScrub = () => {
      // Lerp with 0.08 smoothing factor
      actualTime += (targetTime - actualTime) * 0.08;
      
      // Update only if difference is measurable and video is ready enough
      if (Math.abs(targetTime - actualTime) > 0.002 && video.readyState >= 2) {
        video.currentTime = actualTime;
      }
      requestAnimationFrame(renderScrub);
    };
    requestAnimationFrame(renderScrub);

    // Pinned Hero Timeline
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: heroWrapper,
        start: 'top top',
        end: '+=220%',
        pin: true,
        scrub: 0.85,
        fastScrollEnd: true,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          if (matrixUniforms?.uScroll) {
            matrixUniforms.uScroll.value = self.progress;
          }
          // The video scrubbing mapped to the first 70% of scroll progress
          const scrubProgress = Math.min(self.progress / 0.7, 1);
          const currentDuration = video.duration || 2.4;
          const currentEndTrim = Math.max(startTrim + 0.1, currentDuration - 0.08);
          targetTime = startTrim + scrubProgress * (currentEndTrim - startTrim);
        }
      }
    });

    // 2. "HI" dissolves and rises slightly on scroll
    tl.to(
      hiLayers,
      {
        opacity: 0,
        scale: 1.08,
        y: '-=25',
        duration: 0.25,
        ease: 'power2.in'
      },
      0.03
    );

    // 3. "I'M SIDHEEKHUL AKBAR." smoothly appears across chest
    tl.to(
      nameLayers,
      {
        opacity: 1,
        scale: 1,
        duration: 0.35,
        ease: 'power3.out'
      },
      0.26
    );

    // 4. Subtle nudge of presenter to frame the card
    if (videoContainer) {
      tl.to(
        videoContainer,
        {
          x: '6vw',
          duration: 0.55,
          ease: 'power2.inOut'
        },
        0.22
      );
    }

    // 5. Horizontal card reveals in bottom left
    tl.to(
      sideCard,
      {
        opacity: 1,
        y: 0,
        pointerEvents: 'auto',
        duration: 0.35,
        ease: 'power3.out'
      },
      0.55
    );
  };

  // Run synchronously so the layout pin happens before subsequent ScrollTriggers are created.
  initScrub();

}

export function initContinuousScrollKinetics() {
  // 1. Live Document-Wide 3D Canvas Reactivity (Smooth Inertia Tracking)
  ScrollTrigger.create({
    start: 'top top',
    end: 'max',
    onUpdate: (self) => {
      if (matrixUniforms?.uScroll) {
        // Continuously feeds scroll progress (0 to 1) into your Three.js uniforms
        matrixUniforms.uScroll.value = self.progress;
      }
    }
  });

  // 2. Growth Stack Cards: Kinetic Parallax Floating + Dynamic Zoom (Active Down & Up)
  gsap.utils.toArray('.growth-card').forEach((card, index) => {
    const videoViewport = card.querySelector('.growth-video-viewport');
    const counterEl = card.querySelector('.counter');
    const targetVal = parseFloat(card.dataset.metric || 0);

    // Dynamic Parallax Scrub: Staggers card heights as you scroll up and down
    gsap.fromTo(
      card,
      { y: index % 2 === 0 ? 30 : 60 },
      {
        y: index % 2 === 0 ? -30 : -60,
        ease: 'none',
        scrollTrigger: {
          trigger: card,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.2 // Continuous smooth physics tied to your wheel
        }
      }
    );

    // Video Viewport Micro-Depth Scale: Expands when centered, contracts as you leave
    if (videoViewport) {
      gsap.fromTo(
        videoViewport,
        { scale: 0.94, opacity: 0.85 },
        {
          scale: 1.02,
          opacity: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: card,
            start: 'top 80%',
            end: 'center center',
            scrub: 0.8
          }
        }
      );
    }

    // Telemetry Counter: Live count-up that resets and replays smoothly on reverse scroll
    ScrollTrigger.create({
      trigger: card,
      start: 'top 80%',
      end: 'bottom 20%',
      onEnter: () => animateCounter(counterEl, targetVal),
      onEnterBack: () => animateCounter(counterEl, targetVal)
    });
  });

  // 3. Field Notes Cards: Opposing Lateral Drift (Continuous Scrub)
  gsap.utils.toArray('.note-card').forEach((note, index) => {
    const lateralShift = index % 2 === 0 ? -25 : 25;

    gsap.fromTo(
      note,
      { x: lateralShift, y: 20 },
      {
        x: -lateralShift * 0.5,
        y: -30,
        ease: 'none',
        scrollTrigger: {
          trigger: note,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.4
        }
      }
    );
  });

  // 4. Section Headers: Cinematic Scrubbed Tracking Expansion
  gsap.utils.toArray('.section-title').forEach((title) => {
    gsap.fromTo(
      title,
      { letterSpacing: '-0.06em', opacity: 0.7 },
      {
        letterSpacing: '-0.02em',
        opacity: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: title,
          start: 'top 90%',
          end: 'top 40%',
          scrub: 0.6
        }
      }
    );
  });

  // 5. Force Refresh on Load
  if (document.fonts) {
    document.fonts.ready.then(() => {
      ScrollTrigger.refresh();
    });
  }
  window.addEventListener('load', () => {
    ScrollTrigger.refresh();
  });

  // 6. Initialize scroll-aware URL synchronization
  initUrlSync();
}

function animateCounter(el, target) {
  if (!el) return;
  const isFloat = target % 1 !== 0;

  // Use the DOM element itself as the target for the tween so GSAP can 
  // automatically overwrite any previous/ongoing counter animations on it.
  gsap.killTweensOf(el);
  
  el.counterVal = 0;
  gsap.to(el, {
    counterVal: target,
    duration: 1,
    ease: 'power2.out',
    onUpdate: () => {
      el.textContent = isFloat ? el.counterVal.toFixed(1) : Math.round(el.counterVal);
    }
  });
}

function initUrlSync() {
  let allowUrlUpdate = false;
  
  // Enable updates shortly after full load to protect the initial direct-link navigation
  window.addEventListener('load', () => {
    setTimeout(() => {
      allowUrlUpdate = true;
    }, 150);
  });

  const sections = [
    { el: document.getElementById('hero-pinned-wrapper'), url: window.location.pathname },
    ...Array.from(document.querySelectorAll('section[id]')).map(sec => ({
      el: sec,
      url: `#${sec.id}`
    }))
  ];

  sections.forEach(sec => {
    if (!sec.el) return;
    ScrollTrigger.create({
      trigger: sec.el,
      start: 'top 50%',
      end: 'bottom 50%',
      onEnter: () => updateUrl(sec.url),
      onEnterBack: () => updateUrl(sec.url)
    });
  });

  function updateUrl(targetUrl) {
    if (!allowUrlUpdate) return;
    
    const currentHash = window.location.hash;
    const currentPath = window.location.pathname;
    
    if (targetUrl === currentPath) {
      // Clear hash if we're on the hero section
      if (currentHash !== '') {
        history.replaceState(null, null, currentPath);
      }
    } else {
      // Set hash for specific sections
      if (currentHash !== targetUrl) {
        history.replaceState(null, null, targetUrl);
      }
    }
  }
}

